import React from 'react';
type Props = {
  height?: number;
  className?: string;
  color?: string;
};
/**
 * SVG recreation of the real TARTA wordmark — thin, elegant geometric
 * sans-serif with hairline strokes and the signature FLAT-TOP A apex.
 * Designed at 100 units tall. Stem weight ≈ 3.2.
 */
export function TartaWordmark({
  height = 28,
  className = '',
  color = 'currentColor'
}: Props) {
  const widths = {
    T: 78,
    A: 88,
    R: 70
  };
  const tracking = 22; // wide letter spacing to match the real mark
  const order = ['T', 'A', 'R', 'T', 'A'] as const;
  const offsets: number[] = [];
  let x = 0;
  order.forEach(l => {
    offsets.push(x);
    x += widths[l] + tracking;
  });
  const totalWidth = x - tracking;
  return <svg viewBox={`0 0 ${totalWidth} 100`} height={height} width={totalWidth / 100 * height} fill={color} className={className} aria-label="TARTA" role="img" data-id="element-242">
      {order.map((letter, i) => <g key={i} transform={`translate(${offsets[i]}, 0)`} data-id="element-243">
          {letter === 'T' && <LetterT data-id="element-244" />}
          {letter === 'A' && <LetterA data-id="element-245" />}
          {letter === 'R' && <LetterR data-id="element-246" />}
        </g>)}
    </svg>;
}
/* ─────────────────────────────────────────────────────────────
 * Letter geometry — 100 tall, hairline stem weight ≈ 3.2
 * All letters built as filled paths so they render crisply
 * at every size without stroke-scaling issues.
 * ───────────────────────────────────────────────────────────── */
// T — width 78. Top bar 0..78 × 0..3.2. Stem 37.4..40.6 × 0..100.
function LetterT() {
  return <>
      <rect x="0" y="0" width="78" height="3.2" data-id="element-247" />
      <rect x="37.4" y="0" width="3.2" height="100" data-id="element-248" />
    </>;
}
// A — width 88. Flat-top apex from x=40 to x=48 (8 wide).
// Left outer edge: (0,100) → (40,0). Right outer: (48,0) → (88,100).
// Inner edges create the hairline strokes for the legs.
// Thin crossbar at y ≈ 60.
function LetterA() {
  // Outer left leg outline (filled diagonal sliver)
  // Outer points: (0,100) → (40,0) → flat-top → inner edge back down
  // Use polygon approach: two thin slanted bars + flat-top connector + crossbar
  const leftLeg = 'M0 100 L40 0 L43.2 0 L3.4 100 Z';
  const rightLeg = 'M48 0 L88 100 L84.6 100 L44.8 0 Z';
  // Flat-top connector across the apex (between x=40 and x=48 at y=0..3.2)
  const apex = 'M40 0 L48 0 L48 3.2 L40 3.2 Z';
  // Crossbar — thin horizontal, sits across both legs at y=58..61.2
  // At y=58 the legs are at x ≈ 40*(58/100)=23.2 (left inner) and 88-23.2=64.8 (right inner)
  // We build the bar between the inner edges of the legs.
  // Left inner edge slope: x = 3.4 + (40 - 3.4)*(1 - y/100)... easier: just draw rect that
  // overlaps slightly into the legs.
  const bar = 'M22 58 L66 58 L66 61.2 L22 61.2 Z';
  return <>
      <path d={leftLeg} data-id="element-249" />
      <path d={rightLeg} data-id="element-250" />
      <path d={apex} data-id="element-251" />
      <path d={bar} data-id="element-252" />
    </>;
}
// R — width 70. Stem 0..3.2 × 0..100.
// Bowl: rounded top from stem at y=0 out to ~x=58, curving down to y=48 back to stem.
// Leg: diagonal from bowl/stem junction (~y=48) to (70,100).
function LetterR() {
  // Stem
  const stem = 'M0 0 H3.2 V100 H0 Z';
  // Bowl outer: from (3.2, 0) curve to (58, 24) to (3.2, 48)
  // Bowl inner (counter): from (3.2, 3.2) curve to (54.8, 24) to (3.2, 44.8)
  // Use even-odd fill to subtract counter.
  const bowl = 'M3.2 0 C 50 0, 60 0, 58 24 C 60 48, 50 48, 3.2 48 L 3.2 44.8 C 46 44.8, 56 44.8, 54.8 24 C 56 3.2, 46 3.2, 3.2 3.2 Z';
  // Leg diagonal — thin slanted bar from (3.2, 46) area to (70, 100)
  const leg = 'M22 46 L70 100 L66.6 100 L18.6 46 Z';
  return <g fillRule="evenodd" data-id="element-253">
      <path d={stem} data-id="element-254" />
      <path d={bowl} data-id="element-255" />
      <path d={leg} data-id="element-256" />
    </g>;
}